import { Icon, IconType } from '../../Icon'
import { Campaign, CampaignAddon } from '../../../gql/types'
import {
  modelTypeLocalizedString,
  videoDurationLocalizedString,
  videoTypeLocalizedString,
  voiceTypeLocalizedString,
} from '../../../utils/GqlStrings'
import {
  mapCampaignAddonsTypeToText,
  mapAspectRatioToText,
  mapAspectRatioToIcon,
  getVideoPlacementDisplayInfo,
} from '../../../utils/mappers'
import { extractUuidFromFileName } from '../../../utils/helpers'
import { BriefTextRow } from '../BriefRow'
import { PdfZoom } from '../../PdfZoom'
import { DownloadFileButton } from '../../DownloadFileButton'

interface IVideoProps {
  campaign: Campaign
  hooks?: string[]
  addons: CampaignAddon[]
  viewMode?: 'brand' | 'creator'
}

export const VideoBrief = ({
  campaign,
  hooks,
  addons,
  viewMode,
}: IVideoProps) => {
  // Check if there's a PDF to display
  const isPdfMimeType = campaign?.requirementsFile?.name?.includes('pdf')
  const hasPdfUrl = !!campaign?.requirementsFile?.url
  const hasPdfName = !!campaign?.requirementsFile?.name
  const hasPdfToShow = isPdfMimeType && hasPdfUrl && hasPdfName
  const pdfUrl = campaign?.requirementsFile?.url
  const pdfName = campaign?.requirementsFile?.name

  // Get video placement display info using clean helper
  const videoPlacementDisplayInfo = getVideoPlacementDisplayInfo(
    campaign?.videoPlacements,
  )

  return (
    <div className='@container'>
      <div className='grid @xl:grid-cols-4 @xl:gap-6 grid-cols-1 gap-2'>
        <div>
          <h3 className='text-base font-semibold leading-6 text-gray-900'>
            Video Brief
          </h3>
        </div>
        <div className='overflow-hidden bg-white shadow rounded-lg col-span-3'>
          <div className='border-t border-gray-200 px-4'>
            <dl className='@md:divide-y @md:divide-gray-200'>
              <BriefTextRow
                title='Type'
                value={videoTypeLocalizedString(campaign.videoType)}
              />
              <BriefTextRow
                title='Model'
                value={modelTypeLocalizedString(campaign.modelType)}
              />
              <BriefTextRow
                title='Voice'
                value={voiceTypeLocalizedString(campaign.voiceType)}
              />
              <BriefTextRow
                title='Duration'
                value={videoDurationLocalizedString(campaign.videoDuration)}
              />
              <BriefTextRow
                title='Aspect Ratio'
                value={
                  <div className='flex items-center'>
                    <Icon
                      type={mapAspectRatioToIcon(campaign.aspectRatio)}
                      className='mr-1 w-6 h-6 text-gray-600'
                      aria-hidden='true'
                    />
                    {mapAspectRatioToText(campaign.aspectRatio)}
                  </div>
                }
              />
              {videoPlacementDisplayInfo && (
                <BriefTextRow
                  title='Video Placement'
                  value={
                    videoPlacementDisplayInfo.iconType ? (
                      <div className='flex items-center'>
                        <Icon
                          type={videoPlacementDisplayInfo.iconType}
                          className='mr-2 w-5 h-5 text-gray-600'
                          aria-hidden='true'
                        />
                        {videoPlacementDisplayInfo.name}
                      </div>
                    ) : (
                      videoPlacementDisplayInfo.name
                    )
                  }
                />
              )}
              <BriefTextRow
                title='Mentions & Scenes'
                value={campaign.requirements}
                formatHTML={true}
              />

              {/* Display PDF if available */}
              {hasPdfToShow && (
                <div className='py-4'>
                  <div className='font-bold text-sm text-gray-500 mb-2'>
                    Brief requirements
                  </div>
                  <div className='group rounded-lg'>
                    <PdfZoom
                      src={pdfUrl}
                      border={false}
                      className='w-full'
                      iframeHeight='500px'
                    />
                    <div className='flex justify-end py-1 px-2 bg-gray-50'>
                      <div className='flex space-x-2'>
                        <DownloadFileButton
                          fileUrl={pdfUrl}
                          fileName={extractUuidFromFileName(pdfName)}
                        />
                        <button
                          onClick={() => window.open(pdfUrl, '_blank')}
                          type='button'
                          className='flex items-center text-gray-400 hover:text-gray-600 focus:outline-none rounded-full bg-white p-1 shadow-sm hover:bg-gray-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-200'
                        >
                          <Icon
                            type={IconType.externalLink}
                            className='h-4 w-4 text-primary'
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {hooks?.length > 0 && (
                <div>
                  <hr />
                  {viewMode === 'creator' && (
                    <p className='mt-4 text-sm text-gray-900'>
                      This campaign requires {hooks.length} extra variation
                      {hooks.length > 1 ? 's' : ''}. Please make sure to create
                      and upload {hooks.length} distinct version
                      {hooks.length > 1 ? 's' : ''} of{' '}
                      {videoDurationLocalizedString(campaign.videoDuration)},
                      each as a separate file. Check the details for each
                      variation below.
                    </p>
                  )}
                  {hooks?.map((hook, index) => (
                    <div key={index}>
                      <BriefTextRow
                        title={`Variation ${index + 1}`}
                        value={hook}
                        formatHTML={true}
                      />
                    </div>
                  ))}
                </div>
              )}
              {addons?.length > 0 && (
                <div>
                  <hr />
                  {viewMode === 'creator' && (
                    <p className='mt-4 text-sm text-gray-900'>
                      This campaign requires {addons.length} extra add-on
                      {addons.length > 1 ? 's' : ''}. Check the details below.
                    </p>
                  )}
                  {addons?.map((addon, index) => (
                    <div key={index}>
                      <BriefTextRow
                        title={`Add-on ${index + 1}`}
                        value={mapCampaignAddonsTypeToText(addon)}
                        formatHTML={true}
                      />
                    </div>
                  ))}
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}
